import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { CommercialScope } from '../../../../types/clientOnboarding';

interface Props {
  data: Partial<CommercialScope>;
  onChange: (data: Partial<CommercialScope>) => void;
  errors: Record<string, string>;
}

const inputCls =
  'w-full p-2.5 border border-[var(--border-light)] rounded-lg bg-[var(--surface)] text-[var(--text-primary)] focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent';
const labelCls = 'block text-sm font-medium text-[var(--text-secondary)] mb-1';

const CommercialScopeStep: React.FC<Props> = ({ data, onChange, errors }) => {
  const { t } = useTranslation(['admin_client_onboarding']);

  return (
    <div className="space-y-4" data-testid="onboarding-step-offer">
      <div>
        <h3 className="text-heading-3 text-[var(--text-primary)] mb-1">{t('step4.heading')}</h3>
        <p className="text-sm text-[var(--text-secondary)]">{t('step4.description')}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className={labelCls} htmlFor="commercial-command-packs">{t('step4.commandPacks')}</label>
          <p className="text-xs text-[var(--text-tertiary)] mb-1">{t('step4.commandPacksDesc')}</p>
          <input id="commercial-command-packs" className={inputCls} type="number" min={0} value={data.command_packs ?? 0}
            onChange={e => onChange({ ...data, command_packs: Number(e.target.value) })}
            aria-describedby={errors['commercial.command_packs'] ? 'commercial-command-packs-err' : undefined}
            data-testid="onboarding-command-packs" />
          {errors['commercial.command_packs'] && <p id="commercial-command-packs-err" className="text-[var(--color-danger)] text-xs mt-1" role="alert">{errors['commercial.command_packs']}</p>}
        </div>
        <div>
          <label className={labelCls} htmlFor="commercial-vision-packs">{t('step4.visionPacks')}</label>
          <p className="text-xs text-[var(--text-tertiary)] mb-1">{t('step4.visionPacksDesc')}</p>
          <input id="commercial-vision-packs" className={inputCls} type="number" min={0} value={data.vision_packs ?? 0}
            onChange={e => onChange({ ...data, vision_packs: Number(e.target.value) })}
            aria-describedby={errors['commercial.vision_packs'] ? 'commercial-vision-packs-err' : undefined}
            data-testid="onboarding-vision-packs" />
          {errors['commercial.vision_packs'] && <p id="commercial-vision-packs-err" className="text-[var(--color-danger)] text-xs mt-1" role="alert">{errors['commercial.vision_packs']}</p>}
        </div>
        <div>
          <label className={labelCls} htmlFor="commercial-ia-packs">{t('step4.iaPacks')}</label>
          <p className="text-xs text-[var(--text-tertiary)] mb-1">{t('step4.iaPacksDesc')}</p>
          <input id="commercial-ia-packs" className={inputCls} type="number" min={0} value={data.ia_packs ?? 0}
            onChange={e => onChange({ ...data, ia_packs: Number(e.target.value) })}
            aria-describedby={errors['commercial.ia_packs'] ? 'commercial-ia-packs-err' : undefined}
            data-testid="onboarding-ia-packs" />
          {errors['commercial.ia_packs'] && <p id="commercial-ia-packs-err" className="text-[var(--color-danger)] text-xs mt-1" role="alert">{errors['commercial.ia_packs']}</p>}
        </div>
        <div>
          <label className={labelCls} htmlFor="commercial-gateways">{t('step4.plannedGateways')}</label>
          <p className="text-xs text-[var(--text-tertiary)] mb-1">{t('step4.plannedGatewaysDesc')}</p>
          <input id="commercial-gateways" className={inputCls} type="number" min={0} value={data.planned_gateways ?? 0}
            onChange={e => onChange({ ...data, planned_gateways: Number(e.target.value) })}
            aria-describedby={errors['commercial.planned_gateways'] ? 'commercial-gateways-err' : undefined}
            data-testid="onboarding-gateways" />
          {errors['commercial.planned_gateways'] && <p id="commercial-gateways-err" className="text-[var(--color-danger)] text-xs mt-1" role="alert">{errors['commercial.planned_gateways']}</p>}
        </div>
      </div>

      <div className="space-y-3">
        <label className="flex items-center gap-3 p-3 border border-[var(--border-light)] rounded-lg cursor-pointer hover:bg-[var(--gray-50)]">
          <input type="checkbox" checked={data.fertigation ?? false}
            onChange={e => onChange({ ...data, fertigation: e.target.checked })}
            className="w-4 h-4 rounded" data-testid="onboarding-fertigation" />
          <div>
            <div className="text-sm font-medium text-[var(--text-primary)]">{t('step4.fertigation')}</div>
            <div className="text-xs text-[var(--text-secondary)]">{t('step4.fertigationDesc')}</div>
          </div>
        </label>

        <label className="flex items-center gap-3 p-3 border border-[var(--border-light)] rounded-lg cursor-pointer hover:bg-[var(--gray-50)]">
          <input type="checkbox" checked={data.pump_control ?? false}
            onChange={e => {
              const enabled = e.target.checked;
              onChange({
                ...data,
                pump_control: enabled,
                // R1C-04: Clear hidden pump power when disabling pump control
                ...(enabled ? {} : { pump_rated_power_kw: undefined }),
              });
            }}
            className="w-4 h-4 rounded" data-testid="onboarding-pump-control" />
          <div>
            <div className="text-sm font-medium text-[var(--text-primary)]">{t('step4.pumpControl')}</div>
            <div className="text-xs text-[var(--text-secondary)]">{t('step4.pumpControlDesc')}</div>
          </div>
        </label>

        {data.pump_control && (
          <div>
            <label className={labelCls} htmlFor="commercial-pump-power">{t('step4.pumpRatedPower')}</label>
            <p className="text-xs text-[var(--text-tertiary)] mb-1">{t('step4.pumpRatedPowerDesc')}</p>
            <input id="commercial-pump-power" className={inputCls} type="number" step="0.1" min={0}
              value={data.pump_rated_power_kw ?? ''}
              onChange={e => onChange({ ...data, pump_rated_power_kw: e.target.value ? Number(e.target.value) : undefined })}
              aria-describedby={errors['commercial.pump_power'] ? 'commercial-pump-power-err' : undefined}
              data-testid="onboarding-pump-power" />
            {errors['commercial.pump_power'] && <p id="commercial-pump-power-err" className="text-[var(--color-danger)] text-xs mt-1" role="alert">{errors['commercial.pump_power']}</p>}
          </div>
        )}
      </div>

      <div className="flex items-start gap-2 p-3 rounded-lg bg-[var(--color-info)]/10 border border-[var(--color-info)]/30 text-sm text-[var(--text-secondary)]" data-testid="onboarding-domain-rule">
        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-[var(--color-info)]" />
        <div>
          <p className="font-medium">{t('step4.domainRuleTitle')}</p>
          <p>{t('step4.domainRule')}</p>
        </div>
      </div>
    </div>
  );
};

export default CommercialScopeStep;
